/**
 * Built-in macro definitions.
 *
 * @module macros/registry
 *
 * Each macro is a pure function that takes parsed args and a resolution context,
 * returning a string. No Effect dependency — these are synchronous utilities.
 */

import { randomUUID } from 'node:crypto';
import type { MacroResolutionContext } from './types.js';
import { MacroArgError } from './types.js';

type MacroFn = (args: string[], ctx: MacroResolutionContext) => string;

const FIRST_NAMES = [
  'Jordan',
  'Alex',
  'Taylor',
  'Morgan',
  'Casey',
  'Riley',
  'Quinn',
  'Avery',
  'Cameron',
  'Dakota',
  'Sage',
  'Rowan',
  'Hayden',
  'Finley',
  'Emery',
  'Reese',
  'Charlie',
  'Drew',
  'Blake',
  'Skyler',
];

const LAST_NAMES = [
  'Chen',
  'Smith',
  'Patel',
  'Kim',
  'Garcia',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Miller',
  'Davis',
  'Wilson',
  'Moore',
  'Taylor',
  'Anderson',
  'Thomas',
  'Jackson',
  'White',
  'Harris',
  'Martin',
];

const ALPHANUMERIC = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

function randomAlphanumeric(length: number): string {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += ALPHANUMERIC[Math.floor(Math.random() * ALPHANUMERIC.length)];
  }
  return result;
}

function randomItem<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function formatDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseIntArg(macroName: string, argName: string, value: string): number {
  const trimmed = value.trim();
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    throw new MacroArgError(macroName, `${argName} must be an integer, got "${trimmed}"`);
  }
  return parsed;
}

const BUILTIN_MACROS: Record<string, MacroFn> = {
  UUID: () => randomUUID(),

  NOW: () => new Date().toISOString(),

  TODAY: () => formatDate(new Date()),

  DATE: (args) => {
    if (args.length !== 1) {
      throw new MacroArgError('DATE', `expected 1 arg (days), got ${args.length}`);
    }
    const days = parseIntArg('DATE', 'days', args[0]!);
    const d = new Date();
    d.setDate(d.getDate() + days);
    return formatDate(d);
  },

  TIMESTAMP: () => String(Date.now()),

  RANDOM_INT: (args, _ctx) => {
    if (args.length !== 2) {
      throw new MacroArgError('RANDOM_INT', `expected 2 args (min, max), got ${args.length}`);
    }
    const min = parseIntArg('RANDOM_INT', 'min', args[0]!);
    const max = parseIntArg('RANDOM_INT', 'max', args[1]!);
    if (min > max) {
      throw new MacroArgError('RANDOM_INT', `min (${min}) must be <= max (${max})`);
    }
    return String(Math.floor(Math.random() * (max - min + 1)) + min);
  },

  RANDOM_STRING: (args) => {
    if (args.length !== 1) {
      throw new MacroArgError('RANDOM_STRING', `expected 1 arg (length), got ${args.length}`);
    }
    const length = parseIntArg('RANDOM_STRING', 'length', args[0]!);
    if (length < 1 || length > 1000) {
      throw new MacroArgError('RANDOM_STRING', `length must be 1–1000, got ${length}`);
    }
    return randomAlphanumeric(length);
  },

  EMAIL: () => {
    const id = randomAlphanumeric(8).toLowerCase();
    return `test-${id}@example-test.com`;
  },

  FIRST_NAME: () => randomItem(FIRST_NAMES),

  LAST_NAME: () => randomItem(LAST_NAMES),

  PHONE: () => {
    const rand = () => Math.floor(Math.random() * 10);
    // Generate +1-555-XXX-XXXX (555 ensures non-real number)
    return `+1-555-${rand()}${rand()}${rand()}-${rand()}${rand()}${rand()}${rand()}`;
  },

  SEQUENCE: (args, ctx) => {
    if (args.length !== 1) {
      throw new MacroArgError('SEQUENCE', `expected 1 arg (name), got ${args.length}`);
    }
    const name = stripQuotes(args[0]!);
    if (!name) {
      throw new MacroArgError('SEQUENCE', 'name cannot be empty');
    }
    const current = ctx.sequenceCounters.get(name) ?? 0;
    const next = current + 1;
    ctx.sequenceCounters.set(name, next);
    return String(next);
  },

  AI: (args, ctx) => {
    if (args.length !== 1) {
      throw new MacroArgError('AI', `expected 1 arg (prompt), got ${args.length}`);
    }
    const prompt = stripQuotes(args[0]!);
    if (!prompt) {
      throw new MacroArgError('AI', 'prompt cannot be empty');
    }
    const cached = ctx.aiResolutions.get(prompt);
    if (cached === undefined) {
      throw new MacroArgError(
        'AI',
        `no cached resolution for prompt "${prompt}" — the AI prepass must run before sync resolution`
      );
    }
    return cached;
  },
};

/** Strip surrounding single or double quotes (and trim) from a macro arg. Exported for the AI prepass. */
export function stripQuotes(value: string): string {
  return value.trim().replace(/^["']|["']$/g, '');
}

/** Look up a built-in macro function by name. Returns undefined if not found. */
export function getMacro(name: string): MacroFn | undefined {
  return BUILTIN_MACROS[name];
}

/** All registered macro names. */
export function getMacroNames(): string[] {
  return Object.keys(BUILTIN_MACROS);
}
